import React, { useEffect, useState } from 'react';
import { getEnquiries, deleteEnquiry, updateEnquiry } from '../../api';
import './Contact.css';
import { FaEye, FaTrash, FaSearch } from "react-icons/fa";

const Contact = () => {
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0, replied: 0, pending: 0 });
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('new-old');

  const [currentPage, setCurrentPage] = useState(1);
  const [viewAll, setViewAll] = useState(false);
  const PER_PAGE = 10;

  const [selectedContact, setSelectedContact] = useState(null);
  const [openCardId, setOpenCardId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchEnquiries = async () => {
    try {
      const data = await getEnquiries();
      if (Array.isArray(data)) {
        setContacts(data);
        setStats({
          total: data.length,
          unread: data.filter((c) => !c.is_read).length,
          replied: data.filter((c) => c.is_replied).length,
          pending: data.filter((c) => !c.is_replied).length,
        });
      }
    } catch (error) {
      console.error('Error fetching enquiries:', error);
    }
  };

  useEffect(() => { fetchEnquiries(); }, []);

  const handleDelete = (id) => setDeleteId(id);

  const confirmDelete = async () => {
    try { await deleteEnquiry(deleteId); fetchEnquiries(); }
    catch (error) { console.error('Delete error:', error); }
    finally { setDeleteId(null); }
  };

  const handleReplyWhatsApp = (contact) => {
    const phone = contact.phone.replace(/\D/g, '');
    const cleanPhone = phone.length === 10 ? `91${phone}` : phone;
    const message = encodeURIComponent(
      `Hello ${contact.name}, thank you for contacting YashviTech regarding "${contact.service}". Our team will get back to you shortly.`
    );
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };

  const handleMarkReplied = async (id) => {
    try { await updateEnquiry(id, { is_replied: true }); fetchEnquiries(); }
    catch (error) { console.error('Update error:', error); }
  };

  const handleView = async (contact) => {
    setSelectedContact(contact);
    if (!contact.is_read) {
      try { await updateEnquiry(contact.id, { is_read: true }); fetchEnquiries(); }
      catch (error) { console.error(error); }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Search + Sort
  const processed = contacts
    .filter((c) => {
      const q = search.toLowerCase();
      return (
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phone?.includes(q) ||
        c.service?.toLowerCase().includes(q) ||
        c.message?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      if (sort === 'new-old') return new Date(b.created_at) - new Date(a.created_at);
      if (sort === 'old-new') return new Date(a.created_at) - new Date(b.created_at);
      if (sort === 'a-z') return (a.name || '').localeCompare(b.name || '');
      if (sort === 'z-a') return (b.name || '').localeCompare(a.name || '');
      return 0;
    });

  const totalPages = Math.ceil(processed.length / PER_PAGE);
  const startIdx = (currentPage - 1) * PER_PAGE;
  const displayed = viewAll ? processed : processed.slice(startIdx, startIdx + PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [search, sort]);

  return (
    <div className="co-container">
      <h1>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="8" r="4" />
          <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7" />
        </svg>
        Contact Messages
      </h1>
      <p className="co-subtitle">Manage all inquiries from yashvitech.com</p>

      {/* Stats */}
      <div className="co-stats">
        <div className="co-stat-card"><span>▣</span><div><p>Total</p><h3>{stats.total}</h3></div></div>
        <div className="co-stat-card"><span>✉</span><div><p>Unread</p><h3>{stats.unread}</h3></div></div>
        <div className="co-stat-card"><span>✓</span><div><p>Replied</p><h3>{stats.replied}</h3></div></div>
        <div className="co-stat-card"><span>◷</span><div><p>Pending</p><h3>{stats.pending}</h3></div></div>
      </div>

      {/* Search + Sort */}
      <div className="co-toolbar">
        <div className="co-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search name, email, phone, message..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <select className="co-filter-select" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="new-old">New to Old</option>
          <option value="old-new">Old to New</option>
          <option value="a-z">Name (A - Z)</option>
          <option value="z-a">Name (Z - A)</option>
        </select>
      </div>

      {/* Desktop Table Container */}
      <div className="co-table-section co-desktop">
        <table className="co-table">
          <thead>
            <tr>
              <th>S.NO</th>
              <th>NAME</th>
              <th>EMAIL</th>
              <th>PHONE</th>
              <th>SERVICE</th>
              <th>MESSAGE</th>
              <th>DATE</th>
              <th>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {displayed.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>No enquiries found</td></tr>
            ) : (
              displayed.map((contact, idx) => (
                <tr key={contact.id}>
                  <td>{startIdx + idx + 1}</td>
                  <td title={contact.name}>
                    {contact.name}
                    {!contact.is_read && <span className="co-new">NEW</span>}
                  </td>
                  <td title={contact.email}>{contact.email}</td>
                  <td>{contact.phone}</td>
                  <td title={contact.service}><span className="co-service-badge">{contact.service || 'General'}</span></td>
                  <td title={contact.message}>{contact.message}</td>
                  <td>{formatDate(contact.created_at)}</td>
                  <td className="co-actions">
                    <div className="co-actions-inner">
                      <button className="co-btn view" onClick={() => handleView(contact)}><FaEye /></button>
                      <button className="co-btn del" onClick={() => handleDelete(contact.id)}><FaTrash /></button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="co-mobile-cards">
        {displayed.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '20px' }}>No enquiries found</p>
        ) : (
          displayed.map((contact) => {
            const isOpen = openCardId === contact.id;
            return (
              <div key={contact.id} className="co-mobile-card">
                <div className="co-mobile-card-header" onClick={() => setOpenCardId(isOpen ? null : contact.id)}>
                  <div className="co-mobile-card-title">
                    <strong>{contact.name}</strong>
                    {!contact.is_read && <span className="co-new">NEW</span>}
                  </div>
                  <div className="co-mobile-card-right">
                    <span className="co-mobile-date">{formatDate(contact.created_at)}</span>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }}>
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>
                {isOpen && (
                  <div className="co-mobile-card-body">
                    <p><b>Service:</b> <span className="co-service-badge">{contact.service || 'General'}</span></p>
                    <p><b>Email:</b> {contact.email}</p>
                    <p><b>Phone:</b> {contact.phone}</p>
                    <p><b>Message:</b> {contact.message}</p>
                    <div className="co-mobile-actions">
                      <button className="co-btn view" onClick={() => handleView(contact)}><FaEye /></button>
                      <button className="co-btn del" onClick={() => handleDelete(contact.id)}><FaTrash /></button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination Container */}
      {!viewAll && totalPages > 1 && (
        <div className="co-pagination">
          <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>←</button>
          <span>{currentPage} / {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>→</button>
          <button className="view-all" onClick={() => setViewAll(true)}>View All →</button>
        </div>
      )}
      {viewAll && (
        <div className="co-pagination">
          <button className="view-all" onClick={() => { setViewAll(false); setCurrentPage(1); }}>Show Less</button>
        </div>
      )}

      {/* View Modal */}
      {selectedContact && (
        <div className="co-modal-overlay" onClick={() => setSelectedContact(null)}>
          <div className="co-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{selectedContact.name}</h3>
            <p className="co-modal-service">{selectedContact.service}</p>
            <p><b>Email:</b> {selectedContact.email}</p>
            <p><b>Phone:</b> {selectedContact.phone}</p>
            <p><b>Date:</b> {formatDate(selectedContact.created_at)}</p>
            <p><b>Status:</b> {selectedContact.is_replied ? 'Replied' : 'Pending'}</p>
            <div className="co-modal-msg">
              <b>Message:</b>
              <p>{selectedContact.message || 'No message'}</p>
            </div>
            <div className="co-modal-actions">
              <button className="co-btn whatsapp" onClick={() => handleReplyWhatsApp(selectedContact)}>Reply on WhatsApp</button>
              {!selectedContact.is_replied && (
                <button className="co-btn mark" onClick={() => { handleMarkReplied(selectedContact.id); setSelectedContact(null); }}>Mark as Replied</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteId && (
        <div className="co-modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="co-modal co-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Enquiry?</h3>
            <p>This action cannot be undone.</p>
            <div className="co-modal-actions">
              <button className="co-btn cancel" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="co-btn del" onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;